/** Teacher-only content records. Never serialize provenance into student app source. */
export const MATERIAL_CONTENT_LIMITS = Object.freeze({
  minCards: 4, maxCards: 24, title: 100, language: 35,
  term: 120, answer: 500, id: 48, quote: 1200, location: 160,
});

export type ContentOrigin =
  | { kind: 'source'; segmentId: string; quote: string; location: string }
  | { kind: 'ai_suggested' }
  | { kind: 'teacher_edited' };

export interface FlipCardContentCard {
  id: string;
  term: string;
  answer: string;
  termOrigin: ContentOrigin;
  answerOrigin: ContentOrigin;
}

export interface FlipCardContent {
  schemaVersion: 1;
  templateSlug: 'flip-card-drill';
  title: string;
  language: string;
  cards: FlipCardContentCard[];
}

export interface ContentValidationIssue { path: string; message: string }
export class ContentValidationError extends Error {
  constructor(readonly issues: ContentValidationIssue[]) {
    super(issues.map(issue => `${issue.path}: ${issue.message}`).join(' '));
    this.name = 'ContentValidationError';
  }
}

const ID_PATTERN = /^[A-Za-z][A-Za-z0-9_-]{0,47}$/;
// Hidden direction controls can make the teacher approve different-looking text.
// Normal newlines/tabs remain available for curriculum, but other controls fail closed.
const UNSAFE_CONTROLS = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]/u;

function issue(path: string, message: string): never {
  throw new ContentValidationError([{ path, message }]);
}

function object(value: unknown, keys: readonly string[], path: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || (Object.getPrototypeOf(value) !== Object.prototype && Object.getPrototypeOf(value) !== null)) {
    return issue(path, 'Use a plain content object.');
  }
  const actual = Object.keys(value);
  if (actual.length !== keys.length || actual.some(key => !keys.includes(key))) {
    return issue(path, 'The content has missing or unsupported fields.');
  }
  for (const key of keys) {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (!descriptor || !Object.hasOwn(descriptor, 'value')) return issue(path, 'Use plain content values.');
  }
  return value as Record<string, unknown>;
}

function boundedText(value: unknown, max: number, path: string, normalize = true): string {
  if (typeof value !== 'string' || value.length > max || UNSAFE_CONTROLS.test(value)) {
    return issue(path, `Use text of at most ${max} characters without hidden controls.`);
  }
  const clean = normalize ? value.trim().normalize('NFC') : value.trim();
  if (!clean || clean.length > max) return issue(path, `Enter text of 1 to ${max} characters.`);
  return clean;
}

function identifier(value: unknown, path: string): string {
  if (typeof value !== 'string' || !ID_PATTERN.test(value)) return issue(path, 'Use a valid item identifier.');
  return value;
}

function origin(value: unknown, path: string): ContentOrigin {
  if (!value || typeof value !== 'object') return issue(path, 'Choose a supported content origin.');
  const descriptor = Object.getOwnPropertyDescriptor(value, 'kind');
  const kind = descriptor && Object.hasOwn(descriptor, 'value') ? descriptor.value : undefined;
  if (kind === 'source') {
    const record = object(value, ['kind', 'segmentId', 'quote', 'location'], path);
    return {
      kind,
      segmentId: identifier(record.segmentId, `${path}.segmentId`),
      // Keep literal evidence intact; the generator checks it against its source segment.
      quote: boundedText(record.quote, MATERIAL_CONTENT_LIMITS.quote, `${path}.quote`, false),
      location: boundedText(record.location, MATERIAL_CONTENT_LIMITS.location, `${path}.location`),
    };
  }
  if (kind === 'ai_suggested' || kind === 'teacher_edited') {
    object(value, ['kind'], path);
    return { kind };
  }
  return issue(path, 'Choose a supported content origin.');
}

/** Reject equivalent displayed choices/questions; this cannot establish factual truth. */
export function normalizedContentKey(text: string): string {
  return text.normalize('NFKC').toLowerCase().replace(/\p{Default_Ignorable_Code_Point}/gu, '').replace(/\p{P}/gu, ' ').replace(/\s+/gu, ' ').trim();
}

/** Structural validation only. Literal source evidence is checked against ingestion server-side. */
export function parseFlipCardContent(input: unknown): FlipCardContent {
  const record = object(input, ['schemaVersion', 'templateSlug', 'title', 'language', 'cards'], 'content');
  if (record.schemaVersion !== 1 || record.templateSlug !== 'flip-card-drill') {
    return issue('content', 'This content version or activity is not supported.');
  }
  const title = boundedText(record.title, MATERIAL_CONTENT_LIMITS.title, 'title');
  const language = boundedText(record.language, MATERIAL_CONTENT_LIMITS.language, 'language');
  let canonicalLanguage: string;
  try { canonicalLanguage = Intl.getCanonicalLocales(language)[0]!; }
  catch { return issue('language', 'Use a language tag such as en, es, or fr-CA.'); }
  if (!Array.isArray(record.cards)
    || record.cards.length < MATERIAL_CONTENT_LIMITS.minCards
    || record.cards.length > MATERIAL_CONTENT_LIMITS.maxCards) {
    return issue('cards', `Use ${MATERIAL_CONTENT_LIMITS.minCards} to ${MATERIAL_CONTENT_LIMITS.maxCards} cards.`);
  }
  const ids = new Set<string>();
  const terms = new Set<string>();
  const answers = new Set<string>();
  const cards = Array.from(record.cards, (value, index): FlipCardContentCard => {
    const path = `cards[${index}]`;
    const card = object(value, ['id', 'term', 'answer', 'termOrigin', 'answerOrigin'], path);
    const id = identifier(card.id, `${path}.id`);
    if (ids.has(id)) return issue(`${path}.id`, 'Each card needs a different identifier.');
    ids.add(id);
    const term = boundedText(card.term, MATERIAL_CONTENT_LIMITS.term, `${path}.term`);
    const answer = boundedText(card.answer, MATERIAL_CONTENT_LIMITS.answer, `${path}.answer`);
    const termKey = normalizedContentKey(term);
    const answerKey = normalizedContentKey(answer);
    if (!termKey || terms.has(termKey)) return issue(`${path}.term`, 'Each card needs a distinct term.');
    if (!answerKey || answers.has(answerKey)) return issue(`${path}.answer`, 'Each card needs a distinct answer.');
    terms.add(termKey);
    answers.add(answerKey);
    return { id, term, answer, termOrigin: origin(card.termOrigin, `${path}.termOrigin`), answerOrigin: origin(card.answerOrigin, `${path}.answerOrigin`) };
  });
  return { schemaVersion: 1, templateSlug: 'flip-card-drill', title, language: canonicalLanguage, cards };
}

export function validateFlipCardContent(input: unknown):
  | { ok: true; content: FlipCardContent }
  | { ok: false; issues: ContentValidationIssue[] } {
  try { return { ok: true, content: parseFlipCardContent(input) }; }
  catch (error) {
    if (error instanceof ContentValidationError) return { ok: false, issues: error.issues };
    throw error;
  }
}

const textSchema = (maxLength: number) => ({ type: 'string', minLength: 1, maxLength });
const originSchema = {
  anyOf: [
    { type: 'object', additionalProperties: false, required: ['kind', 'segmentId', 'quote', 'location'], properties: {
      kind: { type: 'string', const: 'source' }, segmentId: { type: 'string', pattern: ID_PATTERN.source },
      quote: textSchema(MATERIAL_CONTENT_LIMITS.quote), location: textSchema(MATERIAL_CONTENT_LIMITS.location),
    } },
    ...(['ai_suggested', 'teacher_edited'] as const).map(kind => ({ type: 'object', additionalProperties: false, required: ['kind'], properties: { kind: { type: 'string', const: kind } } })),
  ],
};

/** Model format assistance; parseFlipCardContent remains the mandatory authority. */
export const FLIP_CARD_CONTENT_SCHEMA: Record<string, unknown> = {
  type: 'object', additionalProperties: false,
  required: ['schemaVersion', 'templateSlug', 'title', 'language', 'cards'],
  properties: {
    schemaVersion: { type: 'integer', const: 1 },
    templateSlug: { type: 'string', const: 'flip-card-drill' },
    title: textSchema(MATERIAL_CONTENT_LIMITS.title), language: textSchema(MATERIAL_CONTENT_LIMITS.language),
    cards: { type: 'array', minItems: MATERIAL_CONTENT_LIMITS.minCards, maxItems: MATERIAL_CONTENT_LIMITS.maxCards, items: {
      type: 'object', additionalProperties: false, required: ['id', 'term', 'answer', 'termOrigin', 'answerOrigin'],
      properties: { id: { type: 'string', pattern: ID_PATTERN.source }, term: textSchema(MATERIAL_CONTENT_LIMITS.term), answer: textSchema(MATERIAL_CONTENT_LIMITS.answer), termOrigin: originSchema, answerOrigin: originSchema },
    } },
  },
};
